import {BaseMuixTableController} from "~/components/muix-data-grid/controllers/BaseMuixTableController.tsx";
import {makeObservable, override} from "mobx";
import type {GridColDef, GridRowId, GridRowModel, GridSortModel} from "@mui/x-data-grid-premium";
import type {JSONSerializableGridColDef} from "~/components/muix-data-grid/MuiXColumnDefinitions.ts";
import type {BagStore} from "~/stores/BagStore.tsx";
import type AuthStore from "../../../../authentication/AuthStore.tsx";
import {CATEGORY_FILTER_FIELD} from "~/pages/categorization/controllers/CategorizationReviewPageFilterController.ts";
import {
    CATEGORY_FILTER_OPERATORS
} from "~/pages/categorization/components/review-table/review-table-muix/filters/CategoryFilter.tsx";
import type {
    CustomReviewColumnTypes
} from "~/pages/categorization/components/review-table/review-table-muix/columns/ColumnTypes";
import {
    toReviewMuiXColumn
} from "~/pages/categorization/components/review-table/review-table-muix/columns/toReviewMuiXColumn.tsx";
import type {NestedRelatedDataModel} from "~/services/ApiHelpers.ts";
import {flattenRelatedDataFields} from "~/services/ApiHelpers.ts";

export class SCTableController extends BaseMuixTableController {
    constructor(
        private auth: AuthStore,
        protected bagStore: BagStore,
    ) {
        super(bagStore, 'SCTC');
        makeObservable(this, {
            tableColumns: override,
            defaultSortModel: override,
            transformGetRows: override,
        });
    }

    get tableColumns(): GridColDef[] {
        const columns: JSONSerializableGridColDef<CustomReviewColumnTypes>[] = [];
        columns.push({
            field: 's_name',
            serializableType: 'string',
            ...this.bagStore.configStore.getPartialGridColDef('s_name', 'Supplier'),
            googleLink: {type: 'part_with_supplier'},
        });
        if (this.bagStore.categorizationColumns.find(
            c => c.in_use && c.table_name === 'data_source_part' && c.column_name === 'p_id')
        ) columns.push({
            field: 'p_id',
            serializableType: 'string',
            ...this.bagStore.configStore.getPartialGridColDef('p_id', 'Part ID'),
        });
        columns.push({
            field: 'p_spend',
            serializableType: 'number-currency',
            ...this.bagStore.configStore.getPartialGridColDef('p_spend', 'Spend'),
        });
        columns.push({
            field: CATEGORY_FILTER_FIELD,
            serializableType: 'custom-cat',
            align: 'left',
            cellClassName: 'category-cell',
            ...this.bagStore.configStore.getPartialGridColDef('cat', 'Category'),
            filterOperators: CATEGORY_FILTER_OPERATORS,
        });
        if (this.bagStore.configStore.getConfigKey('CATEGORIZATION_FILE_DL')) columns.push({
            field: 'file_download',
            serializableType: 'custom-download-file',
            headerName: 'File',
        });
        const column_fields = columns.map(c => c.field);
        this.bagStore.categorizationColumns.filter(
            c => c.in_use && !['cat_id', 'previous_cat_id'].includes(c.column_name) && !column_fields.includes(c.column_name)
        ).forEach(c => columns.push({
            field: c.column_name,
            serializableType: 'string',
            ...this.bagStore.configStore.getPartialGridColDef(c.column_name, c.column_name),
        }));
        return this.applyColumnOrdering(
            this.applyColumnSettings(
                columns.map(c =>
                    toReviewMuiXColumn({...c, aggregable: false}, this.auth.isMithraStaffGroup))
            )
        );
    }

    get defaultSortModel(): GridSortModel | undefined {
        return [{field: 'p_spend', sort: 'desc'}];
    }

    transformGetRows(rows: GridRowModel[], rowCount: number, groupFields: string[], groupKeys: string[], relatedData: NestedRelatedDataModel): GridRowModel[] {
        if (groupKeys.length === 0)
            this._baseCount = rowCount;
        if (this.autoResize)
            setTimeout(() => this.setAutoResize(true), 50);
        if (this.gridApi)
            this.subscribeStopLoadingAfterRowsSet(this.gridApi);
        return rows.map(row => {
            if (groupFields.length === groupKeys.length)
                flattenRelatedDataFields(row, relatedData);
            return {...row, path: groupKeys.concat(this.dataSourceGetGroupKey(row))} as GridRowModel;
        });
    }

    rowId(row: GridRowModel): GridRowId {
        return String(row.part);
    }
}